import React, { useState, useEffect, useRef, useCallback } from 'react';
import EanScanner from './EanScanner';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, ArrowRight, ScanBarcode, Star } from 'lucide-react';
import BrandLogo from './BrandLogo';
import FuelBadge from './FuelBadge';
import { Button } from './ui/button';
import { productsAPI } from '../services/api';
import { productPath } from '../services/productPath';
import { SearchHighlight, searchTokens, matchesTokens } from './SearchHighlight';
import { useT } from '../services/i18n';
import { prodName } from '../services/catalogI18n';

const matchedAltRef = (p, tokens) => {
  if (!tokens.length || matchesTokens(p.ref, tokens) || matchesTokens(p.name, tokens) || Object.values(p.name_i18n || {}).some(n => matchesTokens(n, tokens)) || matchesTokens(p.brand, tokens)) return null;
  const digits = tokens.join('').replace(/\D/g, '');
  if (digits.length >= 8 && (p.ean || '').replace(/\D/g, '') === digits) return { ref: p.ean, ean: true };
  const rb = p.ref_brands || {};
  const hit = (r) => matchesTokens(r, tokens) || (rb[r] && matchesTokens(`${rb[r]} ${r}`, tokens));
  const alt = (p.refs || []).find(hit);
  if (alt) return { ref: rb[alt] ? `${rb[alt]} ${alt}` : alt, oem: false };
  const oem = (p.oem_refs || []).find(hit);
  return oem ? { ref: rb[oem] ? `${rb[oem]} ${oem}` : oem, oem: true } : null;
};

const SearchBar = ({ className = '' }) => {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [scanOpen, setScanOpen] = useState(false);
  const canScan = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  const onScanned = useCallback(async (code) => {
    setScanOpen(false);
    setTerm(code);
    try {
      const res = await productsAPI.getAll(null, code);
      const items = Array.isArray(res) ? res : (res?.items || []);
      const exact = items.find(p => String(p.ean || '').replace(/\D/g, '') === code);
      if (exact) { toast.success(t('scan.found', { name: exact.name })); navigate(productPath(exact)); return; }
    } catch { /* fall back to search page */ }
    navigate(`/recherche?q=${encodeURIComponent(code)}`);
  }, [navigate, t]);
  const tokens = searchTokens(term);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setSuggestions([]); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      productsAPI.getAll(null, q)
        .then(data => { if (!cancelled) { setSuggestions(data.slice(0, 6)); setOpen(true); } })
        .catch(() => { if (!cancelled) setSuggestions([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [term]);

  useEffect(() => {
    const onClick = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const submit = (value = term) => {
    const q = value.trim();
    if (!q) return;
    setOpen(false);
    navigate(`/recherche?q=${encodeURIComponent(q)}`);
  };

  const openProduct = (p) => {
    setOpen(false);
    setTerm('');
    import('../services/ga').then(({ ga }) => ga.search(term.trim(), 'suggestion', { search_results: suggestions.length, item_id: p.id, item_name: p.name })).catch(() => {});
    navigate(productPath(p));
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && suggestions[activeIndex]) {
        openProduct(suggestions[activeIndex]);
      } else {
        submit();
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex w-full items-stretch">
        <input
          type="text"
          data-testid="header-search-input"
          value={term}
          onChange={(e) => { setTerm(e.target.value); setActiveIndex(-1); }}
          onFocus={() => suggestions.length && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={t('header.searchPlaceholder')}
          className="flex-1 px-4 py-2.5 rounded-l-md text-gray-800 text-sm focus:outline-none"
          autoComplete="off"
        />
        {canScan && (
          <button type="button" onClick={() => setScanOpen(true)} data-testid="header-ean-scan" title={t('scan.title')} aria-label={t('scan.title')}
            className="bg-white text-gray-500 hover:text-orange-600 px-2.5 self-stretch border-l border-gray-100 transition-colors">
            <ScanBarcode size={20} />
          </button>
        )}
        <Button data-testid="header-search-button" onClick={() => submit()} className="bg-orange-500 hover:bg-orange-600 rounded-l-none px-4 sm:px-6 h-auto self-stretch">
          {loading ? <Loader2 size={20} className="animate-spin" /> : <Search size={20} />}
        </Button>
      </div>

      <EanScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetect={onScanned} />
      {open && term.trim().length >= 2 && (
        <div data-testid="header-search-suggestions" className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden z-[60] text-gray-900">
          {suggestions.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-500" data-testid="header-search-no-result">
              {loading ? t('header.searching') : t('header.noResults', { term: term.trim() })}
            </p>
          ) : (
            <ul>
              {suggestions.map((p, idx) => (
                <li key={p.id}>
                  <button
                    type="button"
                    data-testid={`header-search-suggestion-${p.id}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => openProduct(p)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    title={t('header.openProduct')}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-orange-50 ${activeIndex === idx ? 'bg-orange-50' : ''}`}
                  >
                    <img src={p.image} alt="" className="w-10 h-10 object-contain flex-shrink-0 bg-gray-50 rounded border border-gray-100" data-testid={`header-search-thumb-${p.id}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate"><SearchHighlight text={prodName(p, lang)} tokens={tokens} /></p>
                      <p className="text-xs text-gray-500 truncate flex items-center gap-1.5">
                        <BrandLogo brand={p.brand} size="xs" showName={false} testId={`header-search-brand-logo-${p.id}`} />
                        <span className="truncate"><SearchHighlight text={p.brand} tokens={tokens} /> · {t('common.ref')} <SearchHighlight text={p.ref} tokens={tokens} /></span>
                        {p.fuel_types?.length > 0 && <FuelBadge types={p.fuel_types} testId={`header-search-fuel-${p.id}`} />}
                        {Number(p.reviews) > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-gray-700 whitespace-nowrap" data-testid={`header-search-rating-${p.id}`} title={`${Number(p.rating).toFixed(1)}/5 · ${p.reviews}`}>
                            <Star size={10} className="fill-yellow-400 text-yellow-400" /> {Number(p.rating).toFixed(1)} <span className="font-normal text-gray-400">({p.reviews})</span>
                          </span>
                        )}
                      </p>
                      {(() => { const m = matchedAltRef(p, tokens); return m ? <p className="text-[11px] text-blue-700 truncate" data-testid={`header-search-matched-ref-${p.id}`}>{m.ean ? t('header.matchesEan') : t('header.matchesRef', { kind: m.oem ? t('common.oem') : t('common.alternative') })}<SearchHighlight text={m.ref} tokens={tokens} /></p> : null; })()}
                    </div>
                    <span className="text-sm font-bold text-gray-900 whitespace-nowrap">{p.price.toFixed(2)} €</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            data-testid="header-search-see-all"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => submit()}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-orange-600 bg-orange-50/60 hover:bg-orange-100 border-t"
          >
            {t('search.seeAll', { q: term.trim() })} <ArrowRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
